import { themeModeAtom, } from "@bracket/ui/atoms";
import { useAtom, } from "jotai";

import { THEMES, DEFAULT_THEME_NAME, } from "./constants";
import type { ColorOption, ThemeStorageKey, ThemeStorageMap, Theme, ThemeName, ThemePalette, } from "@bracket/ui/types";
import { isThemeName, isThemeStorageKey, } from "../../types"


export const initializeTheme = () => {
	setTheme(getFromStorage("bracket-ui-theme") || DEFAULT_THEME_NAME);
} 



export const getTheme = ( name?: ThemeName, ): Theme => {
	const themeName = name ?? ACTIVE_THEME_NAME;
	if(THEMES[themeName]) return THEMES[themeName];
	throw new Error(`Theme ${themeName} not found`);
}

export const isThemeColor = (color: ColorOption): color is keyof ThemePalette => typeof color === "string" && color in getTheme();

export const setTheme = ( name: ThemeName | (() => ThemeName), ): ThemeName => {
	const themeName = typeof name === "function" ? name() : name;
	setToStorage("bracket-ui-theme", themeName);
	ACTIVE_THEME_NAME = themeName;
	return themeName;
}



/** Retrieves the theme name from localStorage for a given ThemeStorageKey.
 * @template K - Must be a valid ThemeStorageKey ("bracket-ui-theme")
 * @param {K} key - The storage key to retrieve the theme for
 * @returns {ThemeStorageMap[K] | null} - The valid ThemeName if found, otherwise null
 * @throws {Error} If the provided key is not a valid ThemeStorageKey */
export const getFromStorage = <K extends ThemeStorageKey>( key: K, ): ThemeStorageMap[K] | null => {
	if(isThemeStorageKey(key)) {
		const value = globalThis.localStorage.getItem(key);
		return (
			value && key === "bracket-ui-theme" && isThemeName(value) ? value :
			null
		);
	}
	throw new Error(`Invalid theme storage key: ${key}`);
}



/** Stores the given theme name under the provided ThemeStorageKey in localStorage.
 * @template K - Must be a valid ThemeStorageKey ("bracket-ui-theme")
 * @param {K} key - The storage key to store the theme under
 * @param {ThemeStorageMap[K]} value - The valid ThemeName to store
 * @throws {Error} If the key is not a valid ThemeStorageKey or the value is not a valid ThemeName */
export const setToStorage = <K extends ThemeStorageKey>( key: K, value: ThemeStorageMap[K], ) => {
	if(isThemeStorageKey(key)) {
		if(isThemeName(value)) {
			return globalThis.localStorage.setItem(key, value);
		} else {
			throw new Error(`Invalid theme name: ${value}`);
		}
	}
	throw new Error(`Invalid theme storage key: ${key}`);
}



export const themed = () => useAtom(themeModeAtom);

// apparently you can't mutate variables even in a let across pacakges in the newer version(s) of ECMAScript
export let ACTIVE_THEME_NAME: ThemeName = getFromStorage("bracket-ui-theme") || DEFAULT_THEME_NAME;
